import { useMemo } from 'react';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { PlatformLocationFeatureV1 } from '@frontend/api-platform-location-feature';
import { SelfUpgradeActionType } from '../constants/self-upgrade';
import { useFeatureSubscriptionConfig } from './useFeatureSubscriptionConfig';

interface UseFeaturePromoNotificationReturnType {
  isLoading: boolean;
  showNotification: boolean;
  primaryAction: SelfUpgradeActionType;
  dismissActions: PlatformLocationFeatureV1.Types.Action[];
}

export const useFeaturePromoNotification = ({
  featureEnum,
  notificationFeature,
  source,
  locationIds,
}: {
  featureEnum: Feature;
  notificationFeature: PlatformLocationFeatureV1.Types.NotificationFeature;
  source: PlatformLocationFeatureV1.Types.Source;
  locationIds: string[];
}): UseFeaturePromoNotificationReturnType => {
  const {
    hasAdminAccessInAtLeastOne,
    isLoading,
    isBundleUpgradeEligible,
    isFreeTrialEligible,
    isFeatureActiveInAllLocations,
    isUpgradeEligible,
    isUpgradeOrTrialEligible,
    subscriptionDataList,
  } = useFeatureSubscriptionConfig({
    feature: featureEnum,
    locationIds,
  });

  const { data, isLoading: isFeatureNotificationStatusLoading } =
    PlatformLocationFeatureV1.Queries.useCheckFeatureNotificationStatusQuery({
      request: {
        notificationFeature,
        sourceEnum: source,
      },
      options: {
        retry: false,
        enabled: !isLoading && isUpgradeOrTrialEligible,
      },
    });

  const dismissActions = useMemo(() => {
    return (data?.dismissAction ?? [])?.filter(
      (dismissAction) => dismissAction !== PlatformLocationFeatureV1.Types.Action.ACTION_UNKNOWN
    );
  }, [data?.dismissAction]);

  const isMultiUpgrade = subscriptionDataList?.length > 1;

  const primaryAction = useMemo(() => {
    if (!hasAdminAccessInAtLeastOne && !isMultiUpgrade) {
      return SelfUpgradeActionType.REQUEST_UPGRADE;
    } else if (isFreeTrialEligible || isBundleUpgradeEligible) {
      return SelfUpgradeActionType.FREE_TRIAL;
    } else if (isUpgradeEligible) {
      return SelfUpgradeActionType.UPGRADE_NOW;
    } else {
      return SelfUpgradeActionType.SCHEDULE_CALL;
    }
  }, [hasAdminAccessInAtLeastOne, isBundleUpgradeEligible, isFreeTrialEligible, isMultiUpgrade, isUpgradeEligible]);

  return {
    isLoading: isLoading || isFeatureNotificationStatusLoading,
    showNotification: !!dismissActions.length && !isFeatureActiveInAllLocations && isUpgradeOrTrialEligible,
    primaryAction,
    dismissActions,
  };
};
